// src/pages/MpesaPayment.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import { logDonation } from '../services/googleSheetsService';

export default function MpesaPayment() {
  const navigate = useNavigate();
  const location = useLocation();
  const [amount, setAmount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // Get amount from URL params or localStorage
    const params = new URLSearchParams(location.search);
    const amountParam = params.get('amount');
    if (amountParam) {
      setAmount(parseFloat(amountParam));
    } else {
      const storedAmount = localStorage.getItem('donationAmount');
      if (storedAmount) {
        setAmount(parseFloat(storedAmount));
      }
    }

    // Get donor data from localStorage
    const donorData = localStorage.getItem('donationData');
    if (donorData) {
      try {
        const data = JSON.parse(donorData);
        setFullName(data.fullName || '');
        setEmail(data.email || '');
        setPhoneNumber(data.phone || '');
      } catch (e) {
        console.error('Error parsing donor data:', e);
      }
    }

    console.log('📋 MpesaPayment loaded with:', {
      amount,
      phoneNumber,
      fullName,
      email
    });
  }, [location]);

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');
    return cleaned.slice(0, 9);
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsProcessing(true);

    console.log('📤 Form submitted with:', {
      fullName,
      email,
      phoneNumber,
      amount
    });

    // Validate
    if (!fullName.trim()) {
      setError('Please enter your full name');
      setIsProcessing(false);
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      setError('Please enter a valid email address');
      setIsProcessing(false);
      return;
    }
    if (!phoneNumber || phoneNumber.length < 9) {
      setError('Please enter a valid phone number (e.g., 712345678)');
      setIsProcessing(false);
      return;
    }
    if (!amount || amount < 1) {
      setError('Please enter a valid amount');
      setIsProcessing(false);
      return;
    }

    // Clean phone number for M-PESA
    let cleanPhone = phoneNumber.replace(/^0/, '254').replace(/^\+?254/, '254');
    if (!cleanPhone.startsWith('254')) {
      cleanPhone = `254${cleanPhone}`;
    }

    console.log('📱 Clean phone number:', cleanPhone);

    // ✅ Store donation data and log to Database & Google Sheets
    const donationData = {
      fullName: fullName || 'Anonymous',
      email: email || '',
      phone: phoneNumber || '',
      paymentMethod: 'M-PESA (STK Push)',
      donationType: 'one-time',
      amount: amount,
      reference: `DON${Date.now()}`,
      status: 'Pending',
      transactionDate: new Date().toISOString()
    };

    // ✅ Save to Database & Google Sheets
    const logResult = await logDonation(donationData);
    if (logResult.success) {
      console.log('✅ Donation logged successfully (Database + Sheets)');
    } else {
      console.warn('⚠️ Donation logging had issues:', logResult.error);
    }

    // Store for the success page
    localStorage.setItem('donationData', JSON.stringify({
      fullName: fullName || 'Anonymous',
      email: email || '',
      phone: phoneNumber || '',
      paymentMethod: 'M-PESA (STK Push)',
      donationType: 'one-time',
      amount: amount
    }));
    localStorage.setItem('donationAmount', amount.toString());

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://kajiadobrighthorizons.org/api';
      const endpoint = `${apiUrl}/daraja/stkpush`;
      
      console.log('📡 Calling API:', endpoint);
      console.log('📦 Request body:', {
        amount: amount,
        phoneNumber: cleanPhone,
        reference: `DON${Date.now()}`,
        description: 'Donation to Kajiado Bright Horizons'
      });

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: amount,
          phoneNumber: cleanPhone,
          reference: `DON${Date.now()}`,
          description: 'Donation to Kajiado Bright Horizons'
        })
      });

      console.log('📥 Response status:', response.status);

      const data = await response.json();
      console.log('📥 Response data:', data);

      if (data.success) {
        console.log('✅ STK Push sent successfully!');
        console.log('📝 Checkout ID:', data.checkoutRequestID);
        
        // Redirect to DonationSuccess for status polling
        navigate(`/donation-success?reference=${data.checkoutRequestID}&amount=${amount}`);
      } else {
        setError(data.error || 'Payment initiation failed. Please try again.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('❌ M-PESA Error:', error);
      setError(`Error: ${error.message || 'There was an error processing your payment. Please try again.'}`);
      setIsProcessing(false);
    }
  };

  return (
    <>
      <SEO 
        title="Pay with M-PESA - Kajiado Bright Horizons"
        description="Make a secure M-PESA donation to Kajiado Bright Horizons. Enter your phone number to receive the STK Push prompt."
        path="/mpesa-payment"
      />
      
      <div className="mpesa-payment-page">
        <div className="container">
          <div className="mpesa-card">
            {/* Header with M-PESA Logo */}
            <div className="mpesa-header">
              <div className="mpesa-logo">
                <i className="fas fa-mobile-alt"></i>
                <span className="mpesa-brand">M-PESA</span>
              </div>
              <h2>Pay via M-PESA</h2>
              <p className="mpesa-subtitle">Secure payment directly from your phone</p>
            </div>

            <div className="mpesa-body">
              {error && <div className="error-message">{error}</div>}

              <form onSubmit={handleSubmit}>
                {/* Amount Display */}
                <div className="amount-display">
                  <span className="amount-label">Donation Amount</span>
                  <span className="amount-value">KES {amount.toLocaleString()}</span>
                </div>

                {/* Full Name */}
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    disabled={isProcessing}
                  />
                </div>

                {/* Email */}
                <div className="form-group">
                  <label>Email Address *</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isProcessing}
                  />
                </div>

                {/* Phone Number - M-PESA Style */}
                <div className="form-group phone-group">
                  <label>Phone Number *</label>
                  <div className="phone-input-wrapper">
                    <span className="phone-prefix">+254</span>
                    <input
                      type="tel"
                      placeholder="712345678"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      maxLength="9"
                      required
                      disabled={isProcessing}
                    />
                  </div>
                  <small className="input-hint">
                    <i className="fas fa-info-circle"></i>
                    Enter phone number without 0 or +254 (e.g., 712345678)
                  </small>
                </div>

                {/* Optional Message */}
                <div className="form-group">
                  <label>Message (Optional)</label>
                  <textarea
                    rows="2"
                    placeholder="Any special instructions or dedication..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    disabled={isProcessing}
                  />
                </div>

                {/* Pay Button - Direct */}
                <button 
                  type="submit" 
                  className="btn-mpesa"
                  disabled={isProcessing || !amount}
                >
                  {isProcessing ? (
                    <><i className="fas fa-spinner fa-spin"></i> Processing...</>
                  ) : (
                    <><i className="fas fa-mobile-alt"></i> Pay KES {amount.toLocaleString()} via M-PESA</>
                  )}
                </button>

                {/* Security Info */}
                <div className="security-info">
                  <div className="security-icons">
                    <i className="fas fa-lock"></i>
                    <i className="fas fa-shield-alt"></i>
                    <i className="fas fa-check-circle"></i>
                  </div>
                  <p>PCI-DSS Certified • Secure • Encrypted</p>
                </div>
              </form>

              {/* Steps */}
              <div className="mpesa-steps">
                <h4>How it works:</h4>
                <div className="steps-grid">
                  <div className="step-item">
                    <span className="step-icon">📱</span>
                    <span>Enter phone number</span>
                  </div>
                  <div className="step-item">
                    <span className="step-icon">💰</span>
                    <span>Pay via M-PESA</span>
                  </div>
                  <div className="step-item">
                    <span className="step-icon">📲</span>
                    <span>Receive M-PESA prompt</span>
                  </div>
                  <div className="step-item">
                    <span className="step-icon">🔐</span>
                    <span>Enter PIN & complete</span>
                  </div>
                </div>
              </div>

              {/* Donation Security */}
              <div className="donation-security">
                <i className="fas fa-shield-alt"></i>
                <span>Your donation is secure and tax-deductible</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .mpesa-payment-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f0f4f8 0%, #e2e8f0 100%);
          padding: 120px 20px 60px;
        }

        .container {
          width: 100%;
          max-width: 480px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .mpesa-card {
          background: #ffffff;
          border-radius: 24px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .mpesa-card:hover {
          box-shadow: 0 25px 70px rgba(0, 0, 0, 0.16);
        }

        .mpesa-header {
          background: linear-gradient(135deg, #1a365d, #2b6cb0);
          padding: 30px 30px 24px;
          text-align: center;
          position: relative;
        }

        .mpesa-logo {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          margin-bottom: 8px;
        }

        .mpesa-logo i {
          font-size: 2.2rem;
          color: #10b981;
          background: rgba(255, 255, 255, 0.15);
          padding: 12px;
          border-radius: 50%;
        }

        .mpesa-brand {
          font-size: 1.8rem;
          font-weight: 700;
          color: #ffffff;
          letter-spacing: -0.5px;
        }

        .mpesa-header h2 {
          color: #ffffff;
          font-size: 1.5rem;
          margin: 0;
          font-weight: 700;
        }

        .mpesa-subtitle {
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.9rem;
          margin: 4px 0 0;
        }

        .mpesa-body {
          padding: 30px;
        }

        .amount-display {
          background: #f7fafc;
          padding: 16px 20px;
          border-radius: 16px;
          margin-bottom: 24px;
          border: 2px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .amount-display .amount-label {
          color: #718096;
          font-size: 0.9rem;
        }

        .amount-display .amount-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #2563eb;
        }

        .form-group {
          margin-bottom: 18px;
        }

        .form-group label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: #2d3748;
          font-size: 0.9rem;
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 12px 16px;
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          color: #1a202c;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
          outline: none;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 60px;
          font-family: inherit;
        }

        .phone-group .phone-input-wrapper {
          display: flex;
          align-items: center;
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .phone-group .phone-input-wrapper:focus-within {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
        }

        .phone-prefix {
          padding: 12px 12px 12px 16px;
          background: rgba(37, 99, 235, 0.06);
          color: #2d3748;
          font-weight: 600;
          border-right: 1px solid #e2e8f0;
          font-size: 1rem;
        }

        .phone-input-wrapper input {
          flex: 1;
          padding: 12px 16px;
          border: none !important;
          background: transparent !important;
          color: #1a202c;
          font-size: 1rem;
          outline: none;
          box-shadow: none !important;
        }

        .input-hint {
          display: block;
          color: #718096;
          font-size: 0.75rem;
          margin-top: 4px;
        }

        .input-hint i {
          color: #2563eb;
        }

        .btn-mpesa {
          width: 100%;
          padding: 14px;
          background: linear-gradient(135deg, #10b981, #059669);
          border: none;
          border-radius: 12px;
          color: #ffffff;
          font-size: 1.05rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          font-family: 'Poppins', sans-serif;
        }

        .btn-mpesa:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.35);
        }

        .btn-mpesa:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .btn-mpesa i {
          font-size: 1.1rem;
        }

        .security-info {
          text-align: center;
          margin-top: 20px;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
        }

        .security-icons {
          display: flex;
          justify-content: center;
          gap: 16px;
          font-size: 1.2rem;
          color: #718096;
          margin-bottom: 6px;
        }

        .security-icons i {
          transition: color 0.3s ease;
        }

        .security-icons i:hover {
          color: #2563eb;
        }

        .security-info p {
          color: #718096;
          font-size: 0.8rem;
          margin: 0;
        }

        .mpesa-steps {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
        }

        .mpesa-steps h4 {
          color: #1a202c;
          font-size: 0.95rem;
          margin-bottom: 12px;
          text-align: center;
        }

        .steps-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .step-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          background: #f7fafc;
          border-radius: 10px;
          font-size: 0.85rem;
          color: #2d3748;
        }

        .step-item .step-icon {
          font-size: 1.2rem;
        }

        .donation-security {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-top: 16px;
          color: #4a5568;
          font-size: 0.85rem;
        }

        .donation-security i {
          color: #10b981;
          font-size: 1rem;
        }

        .error-message {
          background: #fff5f5;
          color: #e53e3e;
          padding: 12px 16px;
          border-radius: 12px;
          border: 1px solid #fed7d7;
          margin-bottom: 20px;
          font-size: 0.9rem;
        }

        @media (max-width: 480px) {
          .mpesa-payment-page {
            padding: 100px 10px 40px;
          }

          .mpesa-header {
            padding: 24px 20px 20px;
          }

          .mpesa-header h2 {
            font-size: 1.3rem;
          }

          .mpesa-body {
            padding: 20px;
          }

          .steps-grid {
            grid-template-columns: 1fr;
          }

          .amount-display .amount-value {
            font-size: 1.3rem;
          }
        }
      `}</style>
    </>
  );
}